import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/hooks/useLanguage";
import { logger } from "@/lib/logger";

// Category type definitions
type CategoryType = "genre" | "themes" | "environment";

// Story data structure type
interface StoryData {
  id: string;
  title: string;
  is_public: boolean;
  finished_at: string | null;
  genre: string[];
  custom_genre: string[];
  themes: string[];
  custom_themes: string[];
  environment: string[];
  custom_environment: string[];
  [key: string]: any; // Allow dynamic access to properties
}

export const StoryStatsTab = () => {
  const { t } = useLanguage();
  const [primaryAxis, setPrimaryAxis] = useState<CategoryType>("genre");
  const [secondaryAxis, setSecondaryAxis] = useState<CategoryType>("themes");

  // Original query for basic user stats
  const { data: stats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['userStats'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_stats')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return data;
    }
  });

  // Separate query for story data needed for category visualization
  const { data: stories, isLoading: isLoadingStories } = useQuery({
    queryKey: ['userStoryCategories', primaryAxis, secondaryAxis],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Fetch stories with categories
      const { data, error } = await supabase
        .from('stories')
        .select(`
          id, title, is_public, finished_at,
          genre, custom_genre, 
          themes, custom_themes, 
          environment, custom_environment
        `)
        .eq('user_id', user.id);

      if (error) throw error;
      return data as StoryData[];
    }
  });

  // Show loading state if either query is loading
  if (isLoadingStats || isLoadingStories) {
    return <div className="text-white/60">{t('dashboard.stats.storyStats.loading')}</div>;
  }

  // Process data for basic stats chart using the original stats data
  const chartData = [
    { name: 'Total Stories', value: stats?.total_stories || 0 },
    { name: 'Completed', value: stats?.completed_stories || 0 },
    { name: 'Public', value: stats?.public_stories || 0 },
  ];

  // Helper function to get all unique items from array fields
  const getUniqueCategories = (categoryType: CategoryType): string[] => {
    const standardField = categoryType;
    const customField = `custom_${categoryType}`;
    
    // Combine standard and custom categories
    const allItems = (stories || []).flatMap(story => {
      // Safely access arrays or default to empty arrays
      const standard = Array.isArray(story[standardField]) ? story[standardField] : [];
      const custom = Array.isArray(story[customField]) ? story[customField] : [];
      return [...standard, ...custom];
    });
    
    // Remove duplicates and empty values
    return [...new Set(allItems)].filter(Boolean);
  };

  // Process data for heat map
  const processHeatMapData = () => {
    // Get all unique categories for both axes
    const primaryCategories = getUniqueCategories(primaryAxis);
    const secondaryCategories = getUniqueCategories(secondaryAxis);
    
    // Create frequency map
    const frequencyMap: Record<string, Record<string, number>> = {};
    
    (stories || []).forEach(story => {
      // Get primary categories (standard + custom)
      const primaryItems = [
        ...(Array.isArray(story[primaryAxis]) ? story[primaryAxis] : []), 
        ...(Array.isArray(story[`custom_${primaryAxis}`]) ? story[`custom_${primaryAxis}`] : [])
      ];
      
      // Get secondary categories (standard + custom)
      const secondaryItems = [
        ...(Array.isArray(story[secondaryAxis]) ? story[secondaryAxis] : []), 
        ...(Array.isArray(story[`custom_${secondaryAxis}`]) ? story[`custom_${secondaryAxis}`] : [])
      ];
      
      // Increment count for each combination
      primaryItems.forEach(primary => {
        if (primary) {
          if (!frequencyMap[primary]) {
            frequencyMap[primary] = {};
          }
          
          secondaryItems.forEach(secondary => {
            if (secondary) {
              if (!frequencyMap[primary][secondary]) {
                frequencyMap[primary][secondary] = 0;
              }
              frequencyMap[primary][secondary]++;
            }
          });
        }
      });
    });

    // Convert to array format for the chart
    const heatmapData = Object.entries(frequencyMap).map(([primary, secondaries]) => {
      // Create base object with name
      const dataPoint: Record<string, any> = { name: primary };
      
      // Add count for each secondary category
      secondaryCategories.forEach(secondary => {
        dataPoint[secondary] = secondaries[secondary] || 0;
      });
      
      // Calculate total for sorting - fixed reduce function
      const secondaryValues = Object.values(secondaries) as number[];
      dataPoint.total = secondaryValues.length > 0 ? 
        secondaryValues.reduce((sum, count) => sum + count, 0) : 0;
      
      return dataPoint;
    });
    
    // Sort by total frequency for better visualization
    heatmapData.sort((a, b) => b.total - a.total);
    
    // Return data limited to top items for better display
    return {
      data: heatmapData.slice(0, 12), // Limit to top 12 for readability
      secondaryCategories: secondaryCategories.slice(0, 12) // Limit to top 12
    };
  };

  // Use try-catch to handle any potential errors in data processing
  let heatmapData = [];
  let secondaryCategories: string[] = [];
  
  try {
    const processedData = processHeatMapData();
    heatmapData = processedData.data;
    secondaryCategories = processedData.secondaryCategories;
  } catch (error) {
    logger.error("Error processing heatmap data:", error);
  }
  
  // Generate colors based on value
  const getColorIntensity = (value: number) => {
    if (value === 0) return 'rgba(105, 0, 109, 0.1)';
    const maxValue = 10; // Adjust based on your data
    const intensity = Math.min(value / maxValue, 1);
    return `rgba(0, 0, 0, ${0.3 + intensity * 0.7})`;
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-white/5">
          <h3 className="text-sm font-medium text-gray-400">{t('dashboard.stats.storyStats.totalStories')}</h3>
          <p className="text-2xl font-bold text-white">{stats?.total_stories || 0}</p>
        </Card>
        <Card className="p-4 bg-white/5">
          <h3 className="text-sm font-medium text-gray-400">{t('dashboard.stats.storyStats.completed')}</h3>
          <p className="text-2xl font-bold text-white">{stats?.completed_stories || 0}</p>
        </Card>
        <Card className="p-4 bg-white/5">
          <h3 className="text-sm font-medium text-gray-400">{t('dashboard.stats.storyStats.published')}</h3>
          <p className="text-2xl font-bold text-white">{stats?.public_stories || 0}</p>
        </Card>
        <Card className="p-4 bg-white/5">
          <h3 className="text-sm font-medium text-gray-400">{t('dashboard.stats.storyStats.averageRating')}</h3>
          <p className="text-2xl font-bold text-white">{stats?.avg_story_rating?.toFixed(1) || '0.0'}</p>
        </Card>
      </div>

      <Card className="p-4 sm:p-6 bg-white/5">
        <h3 className="text-lg font-medium text-white mb-4">{t('dashboard.stats.storyStats.categoryRelationships')}</h3>
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <label className="block text-sm text-gray-400 mb-1">{t('dashboard.stats.storyStats.primaryCategory')}</label>
            <Select 
              value={primaryAxis} 
              onValueChange={(value) => setPrimaryAxis(value as CategoryType)}
            >
              <SelectTrigger className="bg-white/10 border-white/20 text-white">
                <SelectValue placeholder={t('dashboard.stats.storyStats.selectCategory')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="genre">{t('dashboard.stats.storyStats.genre')}</SelectItem>
                <SelectItem value="themes">{t('dashboard.stats.storyStats.themes')}</SelectItem>
                <SelectItem value="environment">{t('dashboard.stats.storyStats.environment')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex-1">
            <label className="block text-sm text-gray-400 mb-1">{t('dashboard.stats.storyStats.secondaryCategory')}</label>
            <Select 
              value={secondaryAxis} 
              onValueChange={(value) => setSecondaryAxis(value as CategoryType)}
            >
              <SelectTrigger className="bg-white/10 border-white/20 text-white">
                <SelectValue placeholder={t('dashboard.stats.storyStats.selectCategory')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="genre">{t('dashboard.stats.storyStats.genre')}</SelectItem>
                <SelectItem value="themes">{t('dashboard.stats.storyStats.themes')}</SelectItem>
                <SelectItem value="environment">{t('dashboard.stats.storyStats.environment')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="h-[400px] chart-container">
          {heatmapData.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-400">
              {t('dashboard.stats.storyStats.noDataAvailable')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={heatmapData}
                layout="vertical"
                margin={{
                  top: 10,
                  right: window.innerWidth < 768 ? 0 : 30,
                  left: window.innerWidth < 768 ? -30 : 100,
                  bottom: 30,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis type="number" stroke="#999" />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  stroke="#999" 
                  width={100} 
                  tick={{ fill: '#999', fontSize: 12 }} 
                />
                <Tooltip
                  formatter={(value, name) => [`${value} ${t('dashboard.stats.storyStats.stories')}`, name]}
                  contentStyle={{ 
                    backgroundColor: '#1a1f2c',
                    border: '1px solid #333',
                    borderRadius: '4px',
                    color: '#fff'                    
                  }}
                  labelStyle={{ color: '#fff', fontWeight: 'bold' }}  
                  itemStyle={{ color: '#fff' }}  
                />
                {secondaryCategories.map((category) => (
                  <Bar 
                    key={category} 
                    dataKey={category} 
                    stackId="a" 
                    name={category} 
                    radius={[0, 4, 4, 0]}
                  >
                    {heatmapData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={getColorIntensity(entry[category] || 0)}
                      />
                    ))}
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        
        <div className="mt-4 flex justify-center">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">{t('dashboard.stats.storyStats.lessFrequent')}</span>
            <div className="flex h-4">
              {[0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1].map((opacity, i) => (
                <div 
                  key={i}
                  className="w-5 h-full" 
                  style={{ backgroundColor: `rgba(0, 0, 0, ${opacity})` }}
                />
              ))}
            </div>
            <span className="text-xs text-gray-400">{t('dashboard.stats.storyStats.moreFrequent')}</span>
          </div>
        </div>
        
        <p className="text-xs text-gray-400 text-center mt-2">
          {t('dashboard.stats.storyStats.usageDescription')}
        </p>
      </Card>
    </div>
  );
};
